import React from 'react';
import { Button } from './ui/Button';

interface ConfirmDialogProps {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  confirmVariant?: 'red' | 'cyan' | 'green';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Styled modal confirm dialog — replaces window.confirm() everywhere.
 * No emoji — Lucide/FA icons only. 44px touch targets. Accessible.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'red',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Dialog panel */}
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0a0c14] p-6 shadow-2xl animate-fadeIn">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
            <i className="fa-solid fa-triangle-exclamation text-red-400 text-sm" />
          </div>
          <div>
            <h2
              id="confirm-title"
              className="text-sm font-bold font-display uppercase tracking-wider text-white"
            >
              {title}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="text-xs text-slate-400 leading-relaxed mb-6 pl-12">
          {message}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant={confirmVariant} size="sm" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
